import React from 'react';
import { Order } from '../../pages/Orders';
import { CheckCircleIcon, XCircleIcon, ClockIcon, CalendarIcon, UserIcon, MailIcon, PhoneIcon, ShoppingCartIcon, AlertTriangleIcon, ChevronRightIcon } from 'lucide-react';
interface OrderListProps {
  orders: Order[];
  onOrderClick: (order: Order) => void;
}
export function OrderList({
  orders,
  onOrderClick
}: OrderListProps) {
  if (orders.length === 0) {
    return <div className="p-8 text-center" data-id="element-1358">
        <p className="text-gray-500" data-id="element-1359">No orders found</p>
      </div>;
  }
  // Get status badge with appropriate colors
  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'new':
        return <span className="px-2 py-1 text-xs rounded-full bg-blue-100 text-blue-800" data-id="element-1360">
            New
          </span>;
      case 'in-progress':
        return <span className="px-2 py-1 text-xs rounded-full bg-amber-100 text-amber-800" data-id="element-1361">
            In Progress
          </span>;
      case 'completed':
        return <span className="px-2 py-1 text-xs rounded-full bg-green-100 text-green-800" data-id="element-1362">
            Completed
          </span>;
      case 'cancelled':
        return <span className="px-2 py-1 text-xs rounded-full bg-red-100 text-red-800" data-id="element-1363">
            Cancelled
          </span>;
      case 'open':
        return <span className="px-2 py-1 text-xs rounded-full bg-purple-100 text-purple-800" data-id="element-1364">
            Open
          </span>;
      default:
        return <span className="px-2 py-1 text-xs rounded-full bg-gray-100 text-gray-800" data-id="element-1365">
            {status}
          </span>;
    }
  };
  // Get payment status icon
  const getPaymentStatusIcon = (status: string) => {
    switch (status) {
      case 'paid':
        return <CheckCircleIcon size={16} className="text-green-500" data-id="element-1366" />;
      case 'unpaid':
        return <ClockIcon size={16} className="text-amber-500" data-id="element-1367" />;
      case 'cancelled':
        return <XCircleIcon size={16} className="text-red-500" data-id="element-1368" />;
      default:
        return null;
    }
  };
  return <div className="overflow-x-auto" data-id="element-1369">
      <table className="min-w-full divide-y divide-gray-200" data-id="element-1370">
        <thead className="bg-gray-50" data-id="element-1371">
          <tr data-id="element-1372">
            <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1373">
              Order
            </th>
            <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1374">
              Date
            </th>
            <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1375">
              Customer
            </th>
            <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1376">
              Status
            </th>
            <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1377">
              Payment
            </th>
            <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1378">
              Total
            </th>
            <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1379">
              Items
            </th>
            <th scope="col" className="relative px-4 py-3" data-id="element-1380">
              <span className="sr-only" data-id="element-1381">Actions</span>
            </th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200" data-id="element-1382">
          {orders.map(order => <tr key={order.id} onClick={() => onOrderClick(order)} className="hover:bg-gray-50 cursor-pointer" data-id="element-1383">
              <td className="px-4 py-4 whitespace-nowrap" data-id="element-1384">
                <div className="text-sm font-medium text-gray-900" data-id="element-1385">
                  #{order.id}
                </div>
                {order.transaction && <div className="text-xs text-gray-500 truncate max-w-[120px]" data-id="element-1386">
                    {order.transaction}
                  </div>}
              </td>
              <td className="px-4 py-4 whitespace-nowrap" data-id="element-1387">
                <div className="flex items-center text-sm text-gray-500" data-id="element-1388">
                  <CalendarIcon size={14} className="mr-1" data-id="element-1389" />
                  {order.date}
                </div>
                <div className="flex items-center text-sm text-gray-500" data-id="element-1390">
                  <ClockIcon size={14} className="mr-1" data-id="element-1391" />
                  {order.time}
                </div>
              </td>
              <td className="px-4 py-4" data-id="element-1392">
                <div className="flex items-center" data-id="element-1393">
                  <div className="flex-shrink-0 h-8 w-8 bg-gray-100 rounded-full flex items-center justify-center" data-id="element-1394">
                    <UserIcon size={14} className="text-gray-500" data-id="element-1395" />
                  </div>
                  <div className="ml-3" data-id="element-1396">
                    <div className="text-sm font-medium text-gray-900" data-id="element-1397">
                      {order.customer.name}
                    </div>
                    <div className="flex items-center text-xs text-gray-500" data-id="element-1398">
                      <MailIcon size={12} className="mr-1" data-id="element-1399" />
                      <span className="truncate max-w-[120px]" data-id="element-1400">
                        {order.customer.email}
                      </span>
                    </div>
                    <div className="flex items-center text-xs text-gray-500" data-id="element-1401">
                      <PhoneIcon size={12} className="mr-1" data-id="element-1402" />
                      {order.customer.mobileNumber}
                    </div>
                  </div>
                </div>
              </td>
              <td className="px-4 py-4 whitespace-nowrap" data-id="element-1403">
                {getStatusBadge(order.status)}
              </td>
              <td className="px-4 py-4 whitespace-nowrap" data-id="element-1404">
                <div className="flex items-center" data-id="element-1405">
                  {getPaymentStatusIcon(order.paymentStatus)}
                  <span className="ml-1.5 text-sm text-gray-700 capitalize" data-id="element-1406">
                    {order.paymentStatus}
                  </span>
                </div>
                {order.transactionStatus && <div className="text-xs text-gray-500 capitalize mt-1" data-id="element-1407">
                    {order.transactionStatus}
                  </div>}
              </td>
              <td className="px-4 py-4 whitespace-nowrap text-sm font-medium text-gray-900" data-id="element-1408">
                ${order.total.toFixed(2)}
              </td>
              <td className="px-4 py-4 whitespace-nowrap" data-id="element-1409">
                <div className="flex items-center text-sm text-gray-500" data-id="element-1410">
                  <ShoppingCartIcon size={14} className="mr-1" data-id="element-1411" />
                  <span data-id="element-1412">
                    {order.items.reduce((sum, item) => sum + item.quantity, 0)}{' '}
                    items
                  </span>
                </div>
                <div className="text-xs text-gray-500 truncate max-w-[120px] mt-1" data-id="element-1413">
                  {order.items.map(item => `${item.quantity}x ${item.name}`).join(', ')}
                </div>
              </td>
              <td className="px-4 py-4 whitespace-nowrap text-right text-sm font-medium" data-id="element-1414">
                <ChevronRightIcon size={16} className="text-gray-400" data-id="element-1415" />
              </td>
            </tr>)}
        </tbody>
      </table>
    </div>;
}